import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Slider,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from 'react-toastify';
import ButtonComponent from "../../../components/common/button/Button";
import { updateRiskConfigAction } from "../../../redux/actions/updateRiskConfigSlice";
import { getActiveGlobalRiskConfig } from "../../../redux/actions/activeGlobalRiskConfigSlice";
/* ---------------- DEFAULT STATE ---------------- */
const LOW_PDT_MAX = 99;
const VAR_MIN = -100;
const VAR_MAX = 0;
export const DEFAULTS = {
  high: {
    pdt: [1, 5],
    variance: [-100, -30],
    safetyWeeks: 3,
  },
  medium: {
    pdt: [5, 10],
    variance: [-30, -10],
  },
  low: {
    pdt: [10, 99],
    variance: [-10, 0],
  },
};

/* ---------------- COMMON STYLES ---------------- */

const sliderSX = {
  height: 6,
  color: "#0b5fff",
  "& .MuiSlider-rail": { background: "#dbe4f3", opacity: 1 },
  "& .MuiSlider-thumb": {
    width: 16,
    height: 16,
    border: "2px solid #0b5fff",
    backgroundColor: "#fff",
  },
};

const NumberBox = ({ value, unit }) => (
  <Paper
    elevation={0}
    sx={{
      minWidth: 70,
      textAlign: "center",
      p: 1.2,
      border: "1px solid #d0d7e2",
      borderRadius: 2,
      fontWeight: 700,
    }}
  >
    {value}
    {unit}
  </Paper>
);

/* ---------------- FROM / TO CONTROL ---------------- */

const FromTo = ({
  label,
  value,
  unit,
  step,
  min,
  max,
  onChange,
  showButtons = true,
  disabled = false,
}) => (
  <Box textAlign="center">
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
    >
      {label}
    </Typography>

    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
      {showButtons && (
      <IconButton
        size="small"
        onClick={() => !disabled && onChange(Math.max(min, value - step))}
        disabled={disabled}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      )}

      <NumberBox value={value} unit={unit} />
 {showButtons && (
      <IconButton
        size="small"
        onClick={() => !disabled && onChange(Math.min(max, value + step))}
        disabled={disabled}
      >
        <AddIcon fontSize="small" />
      </IconButton>
 )}
    </Box>
  </Box>
);

/* ---------------- RANGE ROW ---------------- */
const SingleValueRow = ({
  label,
  value,
  unit,
  min,
  max,
  step,
  onChange,
  showButtons = true,
  disabled = false,
}) => (
  <Box mt={3}>
    <Typography fontWeight={600} mb={1}>
      {label}
    </Typography>

    <Box display="flex" alignItems="center" gap={3}>
      <FromTo
        label="VALUE"
        value={value}
        unit={unit}
        step={step}
        min={min}
        max={max}
        onChange={onChange}
        showButtons={showButtons}
        disabled={disabled}
      />

      <Box flex={1}>
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(_, v) => !disabled && onChange(v)}
          disabled={disabled}
          sx={sliderSX}
        />
      </Box>
    </Box>
  </Box>
);
const RangeRow = ({
  label,
  range,
  unit,
  min,
  max,
  step,
  onChange,
  toButtons=true,
  disabled = false,
}) => {
  const safeRange = Array.isArray(range)
    ? [...range].sort((a, b) => a - b)
    : [Math.max(min, Math.min(range, max)), Math.max(min, Math.min(range, max))];
  const [from, to] = safeRange;

  return (
    <Box mt={3}>
      <Typography fontWeight={600} mb={1}>
        {label}
      </Typography>

      <Box display="flex" alignItems="center" gap={3}>
        <FromTo
          label="FROM"
          value={from}
          unit={unit}
          step={step}
          min={min}
          max={Math.max(min, to - step)}
          onChange={(v) => !disabled && onChange([v, to])}
          disabled={disabled}
        />

        <Box flex={1}>
          <Slider
            value={safeRange}
            min={min}
            max={max}
            step={step}
            onChange={(_, v) => !disabled && onChange(v)}
            disabled={disabled}
            sx={sliderSX}
          />
        </Box>

        <FromTo
          label="TO"
          value={to}
          unit={unit}
          step={step}
          min={Math.min(max, from + step)}
          max={max}
          onChange={(v) => !disabled && onChange([from, v])}
          showButtons={toButtons}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
};

/* ---------------- MAIN PAGE ---------------- */


export default function RiskThresholdSliders({ value = DEFAULTS, onChange, showSave = true, disabled = false }) {
  const tooltipText = {
    pdt: "Time to projected stockout expressed as multiples of Planned Delivery Time.",
    variance: "% gap between Supplier Confirmation and Forecast Demand.",
    safety: "Number of consecutive weeks below Safety Stock.",
  };
  const clamp = (val, min, max) => Math.min(max, Math.max(min, val));
  const normalizeRange = (range, min, max) => {
    const arr = Array.isArray(range) ? range : [range, range];
    let from = clamp(arr[0], min, max);
    let to = clamp(arr[1], min, max);
    if (from > to) [from, to] = [to, from];
    return [from, to];
  };
  // Controlled: all state is managed by parent
  const cascade = (level, key, values) => {
    const next = structuredClone(value);

    if (level === "high") {
      next.high[key] = values;

      if (key === "pdt") {
        const highTo = values[1];
        const mediumTo = Math.max(next.medium.pdt[1], highTo + 1);
        next.medium.pdt = [highTo, mediumTo];
        const lowFrom = mediumTo;
        const lowTo = Math.max(next.low.pdt[1], lowFrom + 1);
        next.low.pdt = [lowFrom, lowTo];
      } else if (key === "variance") {
        const [highFrom, highTo] = normalizeRange(values, VAR_MIN, VAR_MAX);
        next.high.variance = [highFrom, highTo];

        const [, curMedTo] = normalizeRange(next.medium.variance, VAR_MIN, VAR_MAX);
        const medFrom = highTo;
        const medTo = clamp(Math.max(medFrom, curMedTo), VAR_MIN, VAR_MAX);
        next.medium.variance = [medFrom, medTo];

        const [, curLowTo] = normalizeRange(next.low.variance, VAR_MIN, VAR_MAX);
        const lowFrom = next.medium.variance[1];
        const lowTo = clamp(Math.max(lowFrom, curLowTo), VAR_MIN, VAR_MAX);
        next.low.variance = [lowFrom, lowTo];
      } else {
        next.medium[key][0] = values[1];
        next.low[key][0] = next.medium[key][1];
      }
    }

    if (level === "medium") {
      next.medium[key] = values;

      if (key === "pdt") {
        const from = values[0];
        const to = Math.max(values[1], from + 1);
        next.medium.pdt = [from, to];
        const lowFrom = to;
        const lowTo = Math.max(next.low.pdt[1], lowFrom + 1);
        next.low.pdt = [lowFrom, lowTo];
      } else if (key === "variance") {
        const [medFromRaw, medToRaw] = normalizeRange(values, VAR_MIN, VAR_MAX);
        const [, highTo] = normalizeRange(next.high.variance, VAR_MIN, VAR_MAX);
        const medFrom = Math.max(highTo, medFromRaw);
        const medTo = clamp(Math.max(medFrom, medToRaw), VAR_MIN, VAR_MAX);
        next.medium.variance = [medFrom, medTo];

        const [, curLowTo] = normalizeRange(next.low.variance, VAR_MIN, VAR_MAX);
        const lowFrom = next.medium.variance[1];
        const lowTo = clamp(Math.max(lowFrom, curLowTo), VAR_MIN, VAR_MAX);
        next.low.variance = [lowFrom, lowTo];
      } else {
        next.low[key][0] = values[1];
      }
    }

    if (level === "low") {
      if (key === "variance") {
        const [lowFromRaw, lowToRaw] = normalizeRange(values, VAR_MIN, VAR_MAX);
        const [, medTo] = normalizeRange(next.medium.variance, VAR_MIN, VAR_MAX);
        const lowFrom = Math.max(medTo, lowFromRaw);
        const lowTo = clamp(Math.max(lowFrom, lowToRaw), VAR_MIN, VAR_MAX);
        next.low.variance = [lowFrom, lowTo];
      } else {
        next.low[key] = values;
      }
    }

    onChange?.(next);
  };

  const dispatch = useDispatch();
  const { loading: saving } = useSelector((state) => state.updateRiskConfig || {});

  const handleSave = async () => {
    const payload = {
      high: {
        pdtFrom: value.high?.pdt?.[0] ?? 0,
        pdtTo: value.high?.pdt?.[1] ?? 0,
        varFrom: Array.isArray(value.high?.variance) ? value.high.variance[0] : value.high?.variance ?? 0,
        varTo: Array.isArray(value.high?.variance) ? value.high.variance[1] : value.high?.variance ?? 0,
        safetyWeeks: value.high?.safetyWeeks ?? 0,
      },
      medium: {
        pdtFrom: value.medium?.pdt?.[0] ?? 0,
        pdtTo: value.medium?.pdt?.[1] ?? 0,
        varFrom: Array.isArray(value.medium?.variance) ? value.medium.variance[0] : value.medium?.variance ?? 0,
        varTo: Array.isArray(value.medium?.variance) ? value.medium.variance[1] : value.medium?.variance ?? 0,
      },
      low: {
        pdtFrom: value.low?.pdt?.[0] ?? 0,
        pdtTo: value.low?.pdt?.[1] ?? 0,
        varFrom: Array.isArray(value.low?.variance) ? value.low.variance[0] : value.low?.variance ?? 0,
        varTo: Array.isArray(value.low?.variance) ? value.low.variance[1] : value.low?.variance ?? 0,
      },
    };

    try {
      const result = await dispatch(updateRiskConfigAction({ data: payload }));
      const msg = result?.payload?.message || result?.payload?.status || "Parameters Modified Successfully";
      toast.success(msg, { autoClose: 4000 });
      dispatch(getActiveGlobalRiskConfig());
    } catch (err) {
      toast.error(err?.message || "Failed to update risk configuration", { autoClose: 4000 });
    }
  };

  const isRangeEqual = (a, b) => Array.isArray(a)
    && Array.isArray(b)
    && a.length === b.length
    && a.every((val, idx) => val === b[idx]);

  const isDefaultValue = (current) => isRangeEqual(current?.high?.pdt, DEFAULTS.high.pdt)
    && isRangeEqual(current?.high?.variance, DEFAULTS.high.variance)
    && current?.high?.safetyWeeks === DEFAULTS.high.safetyWeeks
    && isRangeEqual(current?.medium?.pdt, DEFAULTS.medium.pdt)
    && isRangeEqual(current?.medium?.variance, DEFAULTS.medium.variance)
    && isRangeEqual(current?.low?.pdt, DEFAULTS.low.pdt)
    && isRangeEqual(current?.low?.variance, DEFAULTS.low.variance);

  const handleSetDefaults = () => {
    onChange?.(structuredClone(DEFAULTS));
    toast.success("Parameters are set to default", { autoClose: 4000 });
  };

  const controlsDisabled = saving || disabled;

  return (
    <Box width="100%">
      {/* HIGH */}
      <Card title="HIGH" level="LEVEL 3 : CRITICAL" color="#ff4d4f" disabled={disabled}>
        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>PDT Range</span>
              <Tooltip title={tooltipText.pdt} arrow>
                <IconButton size="small" aria-label="PDT range info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.high.pdt}
          min={0}
          max={50}
          step={1}
          unit="x"
          onChange={(v) => cascade("high", "pdt", v)}
          disabled={disabled}
        />

        <Divider sx={{ my: 3 }}>
          <Chip label="OR" size="small" />
        </Divider>

        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>Variance % (Confirmation - Forecast)</span>
              <Tooltip title={tooltipText.variance} arrow>
                <IconButton size="small" aria-label="Variance info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.high.variance}
          min={VAR_MIN}
          max={VAR_MAX}
          step={1}
          unit="%"
          onChange={(v) => cascade("high", "variance", v)}
          disabled={disabled}
        />

        <Divider sx={{ my: 3 }}>
          <Chip label="OR" size="small" />
        </Divider>

        {/* Safety Weeks */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography fontWeight={600}>
            Safety Stock Breach for Consecutive Weeks
          </Typography>
          <Tooltip title={tooltipText.safety} arrow>
            <IconButton size="small" aria-label="Safety stock info">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" gap={3}>
          <FromTo
            label="VALUE"
            value={value.high.safetyWeeks}
            unit=" Wks"
            step={1}
            min={0}
            max={52}
            onChange={(v) => {
              const next = structuredClone(value);
              next.high.safetyWeeks = v;
              onChange?.(next);
            }}
            disabled={disabled}
          />

          <Box flex={1}>
            <Slider
              min={0}
              max={12}
              value={value.high.safetyWeeks}
              onChange={(_, v) => {
                if (disabled) return;
                const next = structuredClone(value);
                next.high.safetyWeeks = v;
                onChange?.(next);
              }}
              disabled={disabled}
              sx={sliderSX}
            />
          </Box>

          <NumberBox value={`${52} Wks`} />
        </Box>
      </Card>

      {/* MEDIUM */}
      <Card title="MEDIUM" level="LEVEL 2 : WARNING" color="#faad14" disabled={disabled}>
        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>PDT Range</span>
              <Tooltip title={tooltipText.pdt} arrow>
                <IconButton size="small" aria-label="PDT range info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.medium.pdt}
          min={value.high.pdt[1]}
          max={99}
          step={1}
          unit="x"
          onChange={(v) => cascade("medium", "pdt", v)}
          disabled={disabled}
        />

        <Divider sx={{ my: 3 }}>
          <Chip label="OR" size="small" />
        </Divider>

        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>Variance %</span>
              <Tooltip title={tooltipText.variance} arrow>
                <IconButton size="small" aria-label="Variance info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.medium.variance}
          min={value.high.variance[1]}
          max={VAR_MAX}
          step={1}
          unit="%"
          onChange={(v) => cascade("medium", "variance", v)}
          disabled={disabled}
        />
      </Card>

      {/* LOW */}
      <Card title="LOW" level="LEVEL 1 : STABLE" color="#52c41a" disabled={disabled}>
        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>PDT Range</span>
              <Tooltip title={tooltipText.pdt} arrow>
                <IconButton size="small" aria-label="PDT range info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.low.pdt}
          min={value.medium.pdt[1]}
          max={LOW_PDT_MAX}
          step={1}
          unit="x"
          onChange={(v) => cascade("low", "pdt", v)}
          disabled={disabled}
        />

        <Divider sx={{ my: 3 }}>
          <Chip label="OR" size="small" />
        </Divider>

        <RangeRow
          label={(
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <span>Variance %</span>
              <Tooltip title={tooltipText.variance} arrow>
                <IconButton size="small" aria-label="Variance info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          range={value.low.variance}
          min={value.medium.variance[1]}
          max={VAR_MAX}
          step={1}
          unit="%"
          onChange={(v) => cascade("low", "variance", v)}
          disabled={disabled}
        />
      </Card>
      {showSave && (
        <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
          <ButtonComponent
            label="Set to Default"
            onClick={handleSetDefaults}
            type="secondary"
            bgColor="#f0f0f0"
            color="#111111"
            disabled={controlsDisabled || isDefaultValue(value)}
          />
          <ButtonComponent 
            label={saving ? "Saving..." : "Save"}
            onClick={handleSave}
            type="primary"
            bgColor="#e70d0dff"
            color="#ffffff"
            disabled={controlsDisabled}
          />
        </Box>
      )}
    </Box>
  );
}

/* ---------------- CARD WRAPPER ---------------- */

const Card = ({ title, level, color, children, disabled = false }) => (
  <Paper
    elevation={2}
    sx={{
      p: 4,
      mb: 4,
      borderRadius: 4,
      cursor: disabled ? "not-allowed" : "default",
    }}
  >
    <Box display="flex" justifyContent="space-between" mb={3}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Chip
        label={level}
        variant="outlined"
        sx={{ color, borderColor: color, fontWeight: 600 }}
      />
    </Box>
    {children}
  </Paper>
);